/**
 * Request Deduplication System
 * 
 * Prevents duplicate in-flight requests to the same endpoint.
 * When a request is already in-flight, subsequent identical requests
 * will reuse the same promise instead of making a new request.
 * 
 * This is especially useful for:
 * - React component mounting (double-strict mode)
 * - Rapid user actions triggering the same API call
 * - Multiple components requesting the same data simultaneously
 */

import { HttpResponse } from './http-client';

/**
 * Request key for deduplication
 * Generated from method, URL, and optionally body for POST/PUT/PATCH requests
 */
function generateRequestKey(
  method: string,
  url: string,
  body?: unknown,
): string {
  const methodPart = method.toUpperCase();
  const urlPart = url.toLowerCase();
  
  // For GET requests, just use method + URL
  if (method.toUpperCase() === 'GET') {
    return `${methodPart}:${urlPart}`;
  }
  
  // For POST/PUT/PATCH, include body hash to differentiate requests
  if (body && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT' || method.toUpperCase() === 'PATCH')) {
    const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
    const bodyHash = hashString(bodyStr);
    return `${methodPart}:${urlPart}:${bodyHash}`;
  }
  
  return `${methodPart}:${urlPart}`;
}

/**
 * Simple string hash function (non-cryptographic, suitable for deduplication)
 */
function hashString(str: string): string {
  let hash = 0;
  if (str.length === 0) return '0';
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Request Deduplicator - manages in-flight requests
 */
export class RequestDeduplicator {
  // Map of request key to promise
  private inFlightRequests: Map<string, Promise<HttpResponse<unknown>>> = new Map();
  
  // Statistics for monitoring
  private stats = {
    totalRequests: 0,
    deduplicatedRequests: 0,
    failedRequests: 0,
  };

  /**
   * Check if a request is already in-flight
   */
  isInFlight(method: string, url: string, body?: unknown): boolean {
    const key = generateRequestKey(method, url, body);
    return this.inFlightRequests.has(key);
  }

  /**
   * Get or execute a request with deduplication
   * 
   * If a request with the same method, URL, and body is already in-flight,
   * returns the existing promise. Otherwise, executes the requestFn and
   * caches the promise.
   */
  async executeWithDedup<T = unknown>(
    method: string,
    url: string,
    body: unknown | undefined,
    requestFn: () => Promise<HttpResponse<T>>,
  ): Promise<HttpResponse<T>> {
    const key = generateRequestKey(method, url, body);
    
    this.stats.totalRequests++;
    
    // Return cached promise if request is in-flight
    if (this.inFlightRequests.has(key)) {
      this.stats.deduplicatedRequests++;
      return this.inFlightRequests.get(key) as Promise<HttpResponse<T>>;
    }

    // Execute the request and cache the promise
    const promise = requestFn()
      .then(response => response as HttpResponse<T>)
      .catch(error => {
        this.stats.failedRequests++;
        throw error;
      })
      .finally(() => {
        // Clean up the cached promise when done
        this.inFlightRequests.delete(key);
      });

    // Cache the promise
    this.inFlightRequests.set(key, promise as Promise<HttpResponse<unknown>>);

    return promise;
  }

  /**
   * Clear all cached requests
   * Useful for testing or manual cache invalidation
   */
  clear(): void {
    this.inFlightRequests.clear();
  }

  /**
   * Clear a specific request from cache
   * Useful for manual invalidation of specific endpoints
   */
  clearByKey(method: string, url: string, body?: unknown): void {
    const key = generateRequestKey(method, url, body);
    this.inFlightRequests.delete(key);
  }

  /**
   * Clear requests matching a URL pattern
   * Useful for invalidating all requests to a specific endpoint
   * Example: clearByPattern('GET', 'api/events')
   */
  clearByPattern(method: string, urlPattern: string): void {
    const pattern = new RegExp(urlPattern.toLowerCase());
    const keysToDelete: string[] = [];

    Array.from(this.inFlightRequests.entries()).forEach(([key]) => {
      if (pattern.test(key.toLowerCase())) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.inFlightRequests.delete(key));
  }

  /**
   * Get deduplication statistics
   */
  getStats() {
    return {
      ...this.stats,
      inFlightCount: this.inFlightRequests.size,
      deduplicationRate: this.stats.totalRequests > 0
        ? ((this.stats.deduplicatedRequests / this.stats.totalRequests) * 100).toFixed(2) + '%'
        : '0%',
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalRequests: 0,
      deduplicatedRequests: 0,
      failedRequests: 0,
    };
  }
}

/**
 * Singleton instance for global deduplication
 */
export const requestDeduplicator = new RequestDeduplicator();
